import { useEffect, useState } from 'react';
import { Save, CheckCircle2, AlertCircle, Truck, Package, ToggleLeft, ToggleRight, ExternalLink } from 'lucide-react';

interface CourierConfig {
  enabled: boolean;
  username: string;
  password: string;
  [key: string]: string | boolean;
}
interface AllCouriers {
  fan: CourierConfig & { clientId: string; loginType: string };
  dpd: CourierConfig & { countryCode: string };
  cargus: CourierConfig & { clientId: string };
  gls: CourierConfig & { depotNumber: string; contactName: string };
  sameday: CourierConfig & { pickupPointId: string; awbService: string };
}

const DEFAULT: AllCouriers = {
  fan:     { enabled: false, username: '', password: '', clientId: '', loginType: 'persoana_fizica' },
  dpd:     { enabled: false, username: '', password: '', countryCode: 'RO' },
  cargus:  { enabled: false, username: '', password: '', clientId: '' },
  gls:     { enabled: false, username: '', password: '', depotNumber: '', contactName: '' },
  sameday: { enabled: false, username: '', password: '', pickupPointId: '', awbService: 'Courier' },
};

const COURIERS = [
  {
    key: 'fan', name: 'FAN Courier', logo: '📦',
    color: 'from-blue-600/20 to-blue-800/10', border: 'border-blue-500/30',
    apiUrl: 'https://www.selfawb.ro/',
    docs: 'https://www.fancourier.ro/integrari/',
    fields: [
      { k: 'username',  label: 'Username (email)',  type: 'text',     placeholder: 'user@exemplu.ro' },
      { k: 'password',  label: 'Parolă',            type: 'password', placeholder: '••••••••' },
      { k: 'clientId',  label: 'Număr Client FAN',  type: 'text',     placeholder: 'ex: 12345' },
      { k: 'loginType', label: 'Tip login',         type: 'select',   options: ['persoana_fizica','persoana_juridica'] },
    ],
  },
  {
    key: 'dpd', name: 'DPD Romania', logo: '🚚',
    color: 'from-red-600/20 to-red-800/10', border: 'border-red-500/30',
    apiUrl: 'https://ws.dpd.ro/services',
    docs: 'https://www.dpd.com/ro/ro/shipping-services/business-solutions/',
    fields: [
      { k: 'username',    label: 'Username API',    type: 'text',     placeholder: 'username DPD' },
      { k: 'password',    label: 'Parolă API',      type: 'password', placeholder: '••••••••' },
      { k: 'countryCode', label: 'Cod țară',        type: 'select',   options: ['RO'] },
    ],
  },
  {
    key: 'cargus', name: 'Cargus', logo: '📫',
    color: 'from-orange-600/20 to-orange-800/10', border: 'border-orange-500/30',
    apiUrl: 'https://urgentcargus.azure-api.net',
    docs: 'https://www.cargus.ro/e-commerce/',
    fields: [
      { k: 'username', label: 'Username',     type: 'text',     placeholder: 'username Cargus' },
      { k: 'password', label: 'Parolă',       type: 'password', placeholder: '••••••••' },
      { k: 'clientId', label: 'Client ID',    type: 'text',     placeholder: 'ex: 123456' },
    ],
  },
  {
    key: 'gls', name: 'GLS Romania', logo: '🟡',
    color: 'from-yellow-600/20 to-yellow-800/10', border: 'border-yellow-500/30',
    apiUrl: 'https://api.gls-group.eu',
    docs: 'https://gls-group.eu/RO/ro/trimiteti-colete',
    fields: [
      { k: 'username',    label: 'Username API',   type: 'text',     placeholder: 'username GLS' },
      { k: 'password',    label: 'Parolă API',     type: 'password', placeholder: '••••••••' },
      { k: 'depotNumber', label: 'Număr Depou',    type: 'text',     placeholder: 'ex: 1050' },
      { k: 'contactName', label: 'Nume contact',   type: 'text',     placeholder: 'Persoana contact' },
    ],
  },
  {
    key: 'sameday', name: 'SameDay', logo: '⚡',
    color: 'from-green-600/20 to-green-800/10', border: 'border-green-500/30',
    apiUrl: 'https://api.sameday.ro',
    docs: 'https://github.com/sameday-courier/php-sdk',
    fields: [
      { k: 'username',      label: 'Username',           type: 'text',     placeholder: 'user@exemplu.ro' },
      { k: 'password',      label: 'Parolă',             type: 'password', placeholder: '••••••••' },
      { k: 'pickupPointId', label: 'ID Punct Ridicare',  type: 'text',     placeholder: 'ex: 42' },
      { k: 'awbService',    label: 'Serviciu AWB',       type: 'select',   options: ['Courier','Locker','NextDay','Cargo'] },
    ],
  },
] as const;

type CourierKey = 'fan' | 'dpd' | 'cargus' | 'gls' | 'sameday';

export default function CurieratPage() {
  const [config, setConfig] = useState<AllCouriers>(DEFAULT);
  const [activeTab, setActiveTab] = useState<CourierKey>('fan');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/php-backend/cms-api/curierat.php')
      .then(r => r.json())
      .then(d => setConfig({ ...DEFAULT, ...d }))
      .catch(() => setConfig(DEFAULT));
  }, []);

  const setField = (courier: CourierKey, key: string, value: string | boolean) =>
    setConfig(c => ({ ...c, [courier]: { ...c[courier], [key]: value } }));

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await fetch('/php-backend/cms-api/curierat.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_config', config }),
      });
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Eroare la salvare');
    } finally { setSaving(false); }
  };

  const activeCourier = COURIERS.find(c => c.key === activeTab)!;
  const activeCfg = config[activeTab];
  const enabledCount = COURIERS.filter(c => config[c.key]?.enabled).length;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Truck size={22} /> Curierat & AWB
          </h1>
          <p className="text-white/40 text-sm mt-1">
            Configurare API curieri · {enabledCount} activ{enabledCount !== 1 ? 'i' : ''} din {COURIERS.length}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="flex items-center gap-1.5 text-xs text-green-400 bg-green-500/10 px-3 py-1.5 rounded-lg border border-green-500/20">
              <CheckCircle2 size={13} /> Configurație salvată
            </span>
          )}
          {error && (
            <span className="flex items-center gap-1.5 text-xs text-red-400"><AlertCircle size={13} />{error}</span>
          )}
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            <Save size={15} /> {saving ? 'Se salvează...' : 'Salvează configurația'}
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(74,184,255,0.06)', border: '1px solid rgba(74,184,255,0.12)' }}>
        <p className="text-white/60">
          <span className="text-[#4ab8ff] font-medium">ℹ Cum funcționează:</span> Introduceți credențialele API de la fiecare curier. Generarea AWB din tab-ul
          <strong className="text-white/80"> Comenzi → buton "Generează AWB"</strong> va folosi configurația activă.
          Credențialele sunt stocate criptat pe server și nu sunt vizibile publicului.
        </p>
      </div>

      {/* Courier overview cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {COURIERS.map(c => {
          const cfg = config[c.key];
          return (
            <button key={c.key} onClick={() => setActiveTab(c.key as CourierKey)}
              className={`p-3 rounded-xl border text-left transition-all ${
                activeTab === c.key
                  ? `bg-gradient-to-br ${c.color} ${c.border} border-2`
                  : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
              }`}>
              <div className="text-xl mb-1">{c.logo}</div>
              <div className="text-xs font-semibold text-white">{c.name}</div>
              <div className={`text-[10px] mt-1 font-medium ${cfg?.enabled ? 'text-green-400' : 'text-white/30'}`}>
                {cfg?.enabled ? '● Activ' : '○ Inactiv'}
              </div>
            </button>
          );
        })}
      </div>

      {/* Active courier form */}
      <div className={`bg-gradient-to-br ${activeCourier.color} border ${activeCourier.border} border-2 rounded-2xl p-6 space-y-5`}>
        {/* Courier header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{activeCourier.logo}</span>
            <div>
              <h2 className="text-lg font-bold text-white">{activeCourier.name}</h2>
              <a href={activeCourier.docs} target="_blank" rel="noopener noreferrer"
                className="text-xs text-[#4ab8ff] hover:underline flex items-center gap-1">
                Documentație API <ExternalLink size={10} />
              </a>
            </div>
          </div>
          {/* Enable/disable toggle */}
          <button onClick={() => setField(activeTab, 'enabled', !activeCfg.enabled)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all text-sm font-medium ${
              activeCfg.enabled
                ? 'bg-green-500/20 border-green-500/40 text-green-400'
                : 'bg-white/5 border-white/15 text-white/40 hover:text-white'
            }`}>
            {activeCfg.enabled
              ? <><ToggleRight size={18} /> Activ</>
              : <><ToggleLeft size={18} /> Inactiv</>
            }
          </button>
        </div>

        <div className="h-px bg-white/10" />

        {/* Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {activeCourier.fields.map(field => (
            <div key={field.k}>
              <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">
                {field.label}
              </label>
              {field.type === 'select' ? (
                <select
                  value={String(activeCfg[field.k] ?? '')}
                  onChange={e => setField(activeTab, field.k, e.target.value)}
                  className="w-full bg-[#0d1117] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors [&>option]:bg-[#0d1117] [&>option]:text-white">
                  {'options' in field && field.options.map((o: string) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type}
                  value={String(activeCfg[field.k] ?? '')}
                  onChange={e => setField(activeTab, field.k, e.target.value)}
                  placeholder={field.placeholder}
                  autoComplete={field.type === 'password' ? 'new-password' : 'off'}
                  className="w-full bg-[#0d1117] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors"
                />
              )}
            </div>
          ))}
        </div>

        {/* API endpoint info */}
        <div className="flex items-center gap-2 text-xs text-white/25 pt-2 border-t border-white/10">
          <Package size={12} />
          <span>API Endpoint: <code className="text-white/40">{activeCourier.apiUrl}</code></span>
        </div>

        {/* Status indicator */}
        {activeCfg.username && activeCfg.password ? (
          <div className="flex items-center gap-2 text-xs text-green-400 bg-green-500/10 px-3 py-2 rounded-lg">
            <CheckCircle2 size={13} /> Credențiale introduse. Salvați pentru a activa integrarea.
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-white/30 bg-white/5 px-3 py-2 rounded-lg">
            <AlertCircle size={13} /> Introduceți username și parolă pentru a configura curierul.
          </div>
        )}
      </div>

      {/* Summary table */}
      <div className="bg-[#07090f] border border-white/10 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10">
          <h3 className="text-sm font-semibold text-white/60">Sumar configurații curieri</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5">
              <th className="text-left px-5 py-2.5 text-xs text-white/40 font-medium uppercase tracking-wide">Curier</th>
              <th className="text-left px-4 py-2.5 text-xs text-white/40 font-medium uppercase tracking-wide">Status</th>
              <th className="text-left px-4 py-2.5 text-xs text-white/40 font-medium uppercase tracking-wide">Username</th>
              <th className="text-left px-4 py-2.5 text-xs text-white/40 font-medium uppercase tracking-wide">Parolă</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {COURIERS.map(c => {
              const cfg = config[c.key];
              return (
                <tr key={c.key} className="hover:bg-white/[0.02] cursor-pointer" onClick={() => setActiveTab(c.key as CourierKey)}>
                  <td className="px-5 py-3 flex items-center gap-2">
                    <span>{c.logo}</span>
                    <span className="text-white font-medium">{c.name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg?.enabled ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-white/30'}`}>
                      {cfg?.enabled ? 'Activ' : 'Inactiv'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-white/50 font-mono">
                    {cfg?.username ? cfg.username.slice(0, 20) + (cfg.username.length > 20 ? '…' : '') : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-white/30">
                    {cfg?.password ? '••••••••' : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
